/** Chat notification helpers — extracted from ChatContext to reduce complexity.
 * All functions are side-effectful (sound, browser popup) and safe to no-op if blocked. */
import { toast } from "sonner";
import { logger } from "./logger";

let _audioCtx = null;
function getAudioCtx() {
  try {
    if (_audioCtx) return _audioCtx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    _audioCtx = new Ctx();
    return _audioCtx;
  } catch (e) {
    logger.warn("AudioContext unavailable:", e?.message);
    return null;
  }
}

/** Short two-tone "ping" using the Web Audio API. */
export function playPing() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
  } catch (e) {
    logger.warn("playPing failed:", e?.message);
  }
}

/** Returns true if a desktop notification was actually shown. */
export function showBrowserNotification(sender, body, { forceShow = false } = {}) {
  if (typeof Notification === "undefined") return false;
  if (Notification.permission !== "granted") return false;
  if (!forceShow && document.visibilityState === "visible" && document.hasFocus()) return false;
  try {
    const n = new Notification(`${sender?.name || "New message"} · Hello Foundation`, {
      body: (body || "").slice(0, 140),
      tag: `chat-${sender?.user_id || "msg"}`,
      icon: "/favicon.ico",
    });
    n.onclick = () => { window.focus(); n.close(); };
    return true;
  } catch (e) {
    logger.warn("showBrowserNotification failed:", e?.message);
    return false;
  }
}

/** Surfaces a toast for an incoming message that the user isn't actively viewing. */
export function showIncomingToast(message) {
  toast.message("New message", {
    description: (message?.body || "").slice(0, 120),
    duration: 4500,
    action: {
      label: "Open",
      onClick: () => { window.location.assign(`/chat?with=${message?.sender_id}`); },
    },
  });
}

/** Human-readable description for the "Test notifications" button. */
export function getTestNotificationDescription(popped) {
  if (popped) return "Toast, sound and desktop popup are all live ✓";
  if (typeof Notification === "undefined") {
    return "Toast + sound work. Desktop popups aren't supported by this browser.";
  }
  if (Notification.permission === "denied") {
    return "Toast + sound work. Desktop popups are BLOCKED — re-enable in the browser site settings.";
  }
  return "Toast + sound work. Allow desktop notifications to also get popups.";
}

/** Requests browser notification permission (must be inside a user-gesture handler). */
export async function ensureNotificationPermission() {
  if (typeof Notification === "undefined" || Notification.permission !== "default") return;
  try { await Notification.requestPermission(); } catch (e) { logger.warn("requestPermission failed:", e?.message); }
}
